/** Device detection at startup: the mobile, iOS and installed-app flags, and the mobile first-visit message. */
import { isMobile } from 'pixi.js';
import { uiState } from '../ui/UIState';
import { setSettings, settings } from './Settings';

/** What the device is: a phone or tablet, and whether it runs iOS. */
export interface DeviceInfo {
  mobile: boolean;
  ios: boolean;
}

const deviceState: DeviceInfo = { mobile: false, ios: false };

/**
 * The device, filled by setup before anything reads it. Read-only outside
 * this module, where legacy let anyone write the flags it kept in config.
 */
export const device: Readonly<DeviceInfo> = deviceState;

/**
 * Detects the device and mirrors the result to the UI. Runs once the saved
 * settings have loaded and before they apply, so mouse mode's mobile guard
 * sees a phone on the first load; legacy applied first and detected second.
 */
export function setup(): void {
  detectPlatform();

  // The UI's copies: the mobile flag picks the game screen's layout and
  // mounts the touch controls, the other two tailor the welcome's install tip
  uiState.isMobile = deviceState.mobile;
  uiState.isIos = deviceState.ios;
  uiState.isInstalled = isInstalledApp();

  // Show mobile welcome message on first visit
  if (deviceState.mobile && !settings.mobileshown) {
    uiState.bigMessage = 'mobile';
    setSettings({ mobileshown: true });
  }
}

/** True when launched from the home screen rather than a browser tab. The
 *  manifest asks for `display: fullscreen`, so Chromium reports that mode and
 *  falls back to standalone where fullscreen is refused; iOS honours neither
 *  and only exposes the non-standard `navigator.standalone`. */
function isInstalledApp(): boolean {
  return (
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: standalone)').matches ||
    navigator.standalone === true
  );
}

/**
 * Sets the mobile and iOS flags from Pixi's isMobile, which runs ismobilejs
 * against the real navigator, so an iPad in desktop mode counts as iOS. Phone
 * and tablet are decided from the screen size in Graphics. For testing on a
 * desktop, `?mobile` and `?nomobile` force the mobile flag and sit beside
 * other params; the legacy `#forcemobile` and `#nomobile` hashes still work,
 * but must be the whole hash.
 */
function detectPlatform(): void {
  deviceState.mobile = isMobile.any;
  deviceState.ios = isMobile.apple.device;

  const searchParams = new URLSearchParams(window.location.search);
  if (window.location.hash === '#forcemobile' || searchParams.has('mobile')) {
    deviceState.mobile = true;
  }
  if (window.location.hash === '#nomobile' || searchParams.has('nomobile')) {
    deviceState.mobile = false;
  }
}
